/**
 * Retention Console — admin-only view over the backend document retention
 * policy + SHA-256 duplicate resolution for stored scan documents.
 *
 * Every destructive action is validated SERVER-side (policy eligibility,
 * zero references, retained sibling for duplicate removal) — this page
 * only sends document ids as selection hints.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Archive,
  CheckCircle,
  Copy,
  ShieldCheck,
  Spinner,
  Trash,
  Warning,
} from '@phosphor-icons/react';
import api from '../services/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const fmtBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => (
  <Badge variant="outline" className="text-[10px] capitalize bg-slate-50 text-slate-600">
    {(status || '').replace(/_/g, ' ')}
  </Badge>
);

const AccessDenied = () => (
  <div
    className="bg-amber-50 border border-amber-200 rounded-lg p-6 flex items-start gap-3"
    data-testid="retention-access-denied"
  >
    <ShieldCheck size={24} className="text-amber-600 shrink-0" weight="duotone" />
    <div>
      <h3 className="text-sm font-semibold text-amber-900">Administrator access required</h3>
      <p className="text-xs text-amber-700 mt-1">
        The Retention Console is limited to agency administrators. Ask your admin
        for access if you need documents reviewed or cleaned up.
      </p>
    </div>
  </div>
);

const PolicyBanner = ({ retention }) => (
  <div
    className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3"
    data-testid="retention-policy-banner"
  >
    <Archive size={22} className="text-indigo-700 shrink-0 mt-0.5" weight="duotone" />
    <div className="text-xs text-indigo-800">
      <p className="font-semibold text-sm text-indigo-900 mb-1">Retention policy (server-enforced)</p>
      {retention?.retention_enabled ? (
        <p>
          Abandoned uploads (zero timesheet references, zero claim references, never
          confirmed or submitted) become purge-eligible{' '}
          <strong>{retention.retention_days} days</strong> after abandonment. Confirmed and
          submitted evidence is never deletable here. Every deletion writes a permanent
          audit log entry before any bytes are removed.
        </p>
      ) : (
        <p>Automatic retention cleanup is currently disabled on this server.</p>
      )}
    </div>
  </div>
);

const PurgeConfirmDialog = ({ open, onOpenChange, count, onConfirm, busy }) => {
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Purge {count} eligible document{count === 1 ? '' : 's'}?</DialogTitle>
          <DialogDescription>
            This permanently deletes the stored file bytes for retention-eligible abandoned
            documents. The server re-validates every document against the retention policy —
            anything referenced or inside the retention window is refused. An audit log entry
            is written before each deletion. Type <strong>DELETE</strong> to confirm.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Type DELETE to confirm"
          data-testid="retention-purge-confirm-input"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="retention-purge-cancel-btn">
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={typed !== 'DELETE' || busy}
            onClick={onConfirm}
            data-testid="retention-purge-confirm-btn"
          >
            {busy ? <Spinner size={16} className="animate-spin mr-1" /> : <Trash size={16} className="mr-1" />}
            Purge eligible
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CandidatesTable = ({ retention }) => {
  const items = retention?.candidates || [];
  if (items.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-8 text-center" data-testid="retention-empty-state">
        <CheckCircle size={32} className="mx-auto text-emerald-500 mb-2" />
        <p className="text-sm text-slate-600 font-medium">No abandoned documents</p>
        <p className="text-xs text-slate-400 mt-1">Nothing is awaiting retention cleanup right now.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="max-h-96 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead>Abandoned</TableHead>
              <TableHead>Age</TableHead>
              <TableHead>Refs</TableHead>
              <TableHead>SHA-256</TableHead>
              <TableHead>Eligibility</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((d) => (
              <TableRow key={d.document_id} data-testid={`retention-candidate-${d.document_id}`}>
                <TableCell className="max-w-[220px] truncate text-xs font-medium" title={d.filename}>
                  {d.filename}
                </TableCell>
                <TableCell className="text-xs">{fmtBytes(d.size_bytes)}</TableCell>
                <TableCell className="text-xs">{fmtDate(d.uploaded_at)}</TableCell>
                <TableCell className="text-xs">{fmtDate(d.abandoned_at)}</TableCell>
                <TableCell className="text-xs">{d.age_days != null ? `${d.age_days}d` : '—'}</TableCell>
                <TableCell className="text-xs">
                  {d.timesheet_refs + d.claim_refs === 0 ? '0' : `${d.timesheet_refs} ts / ${d.claim_refs} clm`}
                </TableCell>
                <TableCell className="font-mono text-[10px] text-slate-400">
                  {(d.content_sha256 || '').slice(0, 12)}…
                </TableCell>
                <TableCell>
                  {d.eligible ? (
                    <Badge className="bg-red-100 text-red-700 text-[10px]" data-testid="retention-eligible-badge">
                      Purge-eligible
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-slate-500">
                      In retention window
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const DuplicateGroupCard = ({ group, onResolve, busyDocId }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`dup-group-${group.content_sha256.slice(0, 12)}`}>
    <div className="flex items-center gap-2 mb-3">
      <Copy size={16} className="text-amber-600" />
      <p className="text-sm font-semibold text-slate-800">
        {group.copy_count} byte-identical copies
      </p>
      <span className="font-mono text-[10px] text-slate-400 ml-auto">
        sha256 {group.content_sha256.slice(0, 16)}…
      </span>
    </div>
    <div className="space-y-2">
      {group.documents.map((d) => (
        <div
          key={d.document_id}
          className="flex items-center gap-3 border border-slate-100 rounded-md px-3 py-2"
          data-testid={`dup-doc-${d.document_id}`}
        >
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-slate-800 truncate" title={d.filename}>
              {d.filename}
            </p>
            <p className="text-[11px] text-slate-500">
              {fmtDate(d.uploaded_at)} · {fmtBytes(d.size_bytes)} · {d.timesheet_refs} timesheet ref
              {d.timesheet_refs === 1 ? '' : 's'}
            </p>
          </div>
          {d.is_original && (
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px] shrink-0">Original</Badge>
          )}
          <StatusBadge status={d.document_status} />
          {d.duplicate_resolution?.action === 'dismissed' ? (
            <Badge variant="outline" className="text-[10px] text-slate-500 shrink-0">Dismissed</Badge>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs shrink-0"
                disabled={busyDocId === d.document_id}
                onClick={() => onResolve(d.document_id, 'dismiss')}
                data-testid={`dup-dismiss-${d.document_id}`}
              >
                Dismiss
              </Button>
              {d.removable && (
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-7 text-xs shrink-0"
                  disabled={busyDocId === d.document_id}
                  onClick={() => onResolve(d.document_id, 'remove_duplicate')}
                  data-testid={`dup-remove-${d.document_id}`}
                >
                  {busyDocId === d.document_id ? (
                    <Spinner size={14} className="animate-spin" />
                  ) : (
                    'Remove copy'
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  </div>
);

export default function RetentionConsolePage() {
  const [retention, setRetention] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purging, setPurging] = useState(false);
  const [busyDocId, setBusyDocId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, dupsRes] = await Promise.all([
        api.get('/retention/documents'),
        api.get('/retention/duplicates'),
      ]);
      setRetention(docsRes.data);
      setDuplicates(dupsRes.data.groups || []);
      setDenied(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setDenied(true);
      } else {
        toast.error(err?.response?.data?.detail || 'Failed to load retention data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const eligibleIds = useMemo(
    () => (retention?.candidates || []).filter((c) => c.eligible).map((c) => c.document_id),
    [retention]
  );

  const handlePurge = async () => {
    setPurging(true);
    try {
      const res = await api.post('/retention/purge', { document_ids: eligibleIds });
      const refused = (res.data.results || []).filter((r) => r.status === 'refused');
      toast.success(
        `Purged ${res.data.deleted} document${res.data.deleted === 1 ? '' : 's'}${
          refused.length ? ` · ${refused.length} refused by server policy` : ''
        }`
      );
      setPurgeOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const handleResolve = async (documentId, action) => {
    if (
      action === 'remove_duplicate' &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        'Remove this duplicate copy? A byte-identical original stays retained and an audit log entry is written first.'
      )
    ) {
      return;
    }
    setBusyDocId(documentId);
    try {
      const res = await api.post('/retention/duplicates/resolve', {
        document_id: documentId,
        action,
      });
      toast.success(
        res.data.status === 'removed'
          ? 'Duplicate copy removed — original retained'
          : 'Duplicate dismissed — both copies kept'
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Resolution failed');
    } finally {
      setBusyDocId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="retention-console-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Retention Console</h1>
          <p className="text-sm text-slate-500 mt-1">
            Review retention-eligible scan documents and resolve duplicates — deletions are
            validated and audited server-side
          </p>
        </div>
        {!denied && (
          <Button
            variant="destructive"
            disabled={eligibleIds.length === 0}
            onClick={() => setPurgeOpen(true)}
            data-testid="retention-purge-open-btn"
          >
            <Trash size={16} className="mr-1" />
            Purge eligible ({eligibleIds.length})
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size={28} className="animate-spin text-indigo-600" />
        </div>
      ) : denied ? (
        <AccessDenied />
      ) : (
        <>
          <PolicyBanner retention={retention} />

          <div>
            <h2 className="text-base font-semibold text-slate-800 mb-2">
              Abandoned documents ({retention?.candidates?.length || 0})
            </h2>
            <CandidatesTable retention={retention} />
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-800 mb-2 flex items-center gap-2">
              Duplicate groups ({duplicates.length})
              {duplicates.length > 0 && <Warning size={16} className="text-amber-500" />}
            </h2>
            {duplicates.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-8 text-center" data-testid="dup-empty-state">
                <CheckCircle size={32} className="mx-auto text-emerald-500 mb-2" />
                <p className="text-sm text-slate-600 font-medium">No duplicate documents</p>
                <p className="text-xs text-slate-400 mt-1">
                  No stored documents share a SHA-256 content hash.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {duplicates.map((g) => (
                  <DuplicateGroupCard
                    key={g.content_sha256}
                    group={g}
                    onResolve={handleResolve}
                    busyDocId={busyDocId}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <PurgeConfirmDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        count={eligibleIds.length}
        onConfirm={handlePurge}
        busy={purging}
      />
    </div>
  );
}
